/**
 * Component displaying the usage for the billing page.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright © Ircam Amplify 2025-2026
 */
import Button from '@/components/Button'
import { logger } from '@/config/logger'
import { UsageChart } from '@/pages/Billing/UsageChart'
import {
  getPeriodName,
  UsagePeriodSelect,
} from '@/pages/Billing/UsagePeriodSelect'
import {
  UsageTaskTable,
  UsageTaskTableColumnHeader,
  UsageTaskTableHeaderList,
} from '@/pages/Billing/UsageTaskTable'
import {
  AccountUtil,
  Kpi,
  LogTag,
  TextUtil,
  TierType,
  Util,
  writeCsv,
} from '@amplify3/duplo-back-end'
import { Menu } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import { SelectChangeEvent } from '@mui/material/Select'
import React, { useEffect, useState } from 'react'
import './billing.css'
import { PlanInvoice, TaskDetailList } from './Invoice'
import './UsageTab.css'

const CSV_FILE_NAME_PREFIX = 'usage'
const CSV_FILE_EXT = '.csv'
const EXPORT_MENU_FONT_SIZE = '14px'

export interface UsageTabProps {
  className?: string
  title?: string
  invoice?: PlanInvoice
  kpiList: Kpi[]
}

/**
 * The Usage tab component.
 */
const UsageTab = (props: UsageTabProps) => {
  const fullClassName = `${props.className} usage-table`
  const [selectedKpiIndex, setSelectedKpiIndex] = useState<number | undefined>(
    props.invoice?.taskDetailHistoryList &&
      props.invoice?.taskDetailHistoryList.length > 0
      ? 0
      : undefined
  )
  const [selectedKpi, setSelectedKpi] = useState<TaskDetailList | undefined>(
    props.invoice?.taskDetailHistoryList[0] ?? undefined
  )
  const isKpiHistoryAvailable: boolean =
    !!props.invoice?.taskDetailHistoryList?.length

  /**
   * Return the task list of a KPI instance, sorted alphabetically.
   */
  function sortKpiTaskList(kpi: TaskDetailList) {
    return Object.values(kpi.taskDetailList).sort((a, b) =>
      a.moduleName.localeCompare(b.moduleName)
    )
  }

  // Debug log
  useEffect(() => {
    logger.debug(LogTag.BillingPage, 'selectedKpiIndex:', selectedKpiIndex)
  }, [selectedKpiIndex])

  useEffect(() => {
    logger.debug(LogTag.BillingPage, 'selectedKpi:', selectedKpi)
  }, [selectedKpi])

  /**
   * Handle the change of the selection pf the period.
   *
   * @param index The index of the KPI in the Task Detail history list.
   */
  const handleKpiSelectionIndexChange = (index: number) => {
    if (
      !props.invoice ||
      index >= props.invoice?.taskDetailHistoryList.length
    ) {
      // Component not ready
      setSelectedKpiIndex(undefined)
      setSelectedKpi(undefined)
    } else {
      setSelectedKpiIndex(index)
      setSelectedKpi(props.invoice.taskDetailHistoryList[index])
    }
  }

  /**
   * Handle the change of the selection of the period from the Select component.
   */
  const handleKpiSelectionChange = (event: SelectChangeEvent<number>) => {
    const index = Number(event.target.value)
    handleKpiSelectionIndexChange(index)
  }

  /**
   * The component of the description of the Usage tab.
   */
  const Description = () => {
    return (
      <div className="description">
        Track and export your usage for the selected period.
      </div>
    )
  }

  interface ValueCompProps {
    /**
     * The title
     */
    title: React.ReactNode

    /**
     * The value to display.
     */
    formattedValue?: string

    /**
     * A component to display below the value.
     */
    valueComponent?: React.ReactNode

    /**
     * Optional class name.
     */
    className?: string
  }

  /**
   * The component displaying a label and a value or a control,
   * next to the chart.
   */
  const ValueComp = (props: ValueCompProps) => {
    return (
      <div
        className={`usage-value-container${props.className ? ` ${props.className}` : ''}`}
      >
        <div className="label">{props.title}</div>
        {props.formattedValue && (
          <div className="value">{props.formattedValue}</div>
        )}
        {props.valueComponent && <>{props.valueComponent}</>}
      </div>
    )
  }

  /**
   * Get the default file name of the export CSV file.
   * @param kpi KPI instance of the data to export. Its start date will be used.
   * @param tier The subscription tier.
   * @returns The file name.
   */
  function getCsvFileName(kpi?: TaskDetailList, tier?: TierType): string {
    let periodDate: string
    if (kpi) {
      const date = kpi.periodStart
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = String(date.getFullYear())

      periodDate = `${year}${month}`
      if (!AccountUtil.isTierEnterprise(tier)) {
        periodDate = `${periodDate}${day}`
      }
    } else {
      periodDate = `${new Date().getFullYear()}`
    }

    return `${CSV_FILE_NAME_PREFIX}-${periodDate}${CSV_FILE_EXT}`
  }

  /**
   * Add the data of the given KPI instance to an existing CSV data structure.
   * @param csvData
   *    The CSV data of destination.
   * @param kpi
   *    The KPI instance.
   */
  function addToCsvData(csvData: any[][], kpi: TaskDetailList) {
    let periodString: string
    if (AccountUtil.isTierEnterprise(props.invoice?.tier)) {
      // Enterprise subscription
      periodString = getPeriodName(kpi, props.invoice?.tier)
    } else {
      // Other subscription
      const start = TextUtil.dateToMonthDayWithOrdinal(kpi.periodStart)
      const startYear = kpi.periodStart.getFullYear()
      const end = TextUtil.dateToMonthDayWithOrdinal(kpi.periodEnd)
      const endYear = kpi.periodStart.getFullYear()
      if (startYear === endYear) {
        periodString = `${start} to ${end} ${startYear}`
      } else {
        periodString = `${start} ${startYear} to ${end} ${endYear}`
      }
    }

    for (const task of sortKpiTaskList(kpi)) {
      // Add a new row of data
      csvData.push([
        periodString,
        task.moduleName,
        task.trackProcessedCount,
        Util.absRound(task.consumedCreditCount),
        Util.absRound(task.consumedOffPackageCreditCount),
      ])
    }
  }

  /**
   * Export the usage data to a CSV file.
   *
   * @param kpi
   *    The KPI instance which data will be exported. If undefined, the data
   *    of all the KPI instances the current year are exported.
   * @param filename
   *    The file name of the CSV file. If undefined, the default one is used.
   */
  function exportKpiToCsv(kpi?: TaskDetailList, filename?: string) {
    const csvData: any[][] = [
      [
        'Period',
        ...UsageTaskTableHeaderList.map(
          (header: UsageTaskTableColumnHeader) => header.title
        ),
      ],
    ]

    if (kpi) {
      // Get data for that KPI
      addToCsvData(csvData, kpi)
    } else if (props.invoice) {
      // Get data for all the KPI of the current year
      const thisYear = new Date().getFullYear()
      for (const itKpi of props.invoice.taskDetailHistoryList) {
        if (thisYear === itKpi.periodStart.getFullYear()) {
          addToCsvData(csvData, itKpi)
        }
      }
    }

    // Write CSV file
    const localFileName = filename ?? getCsvFileName(kpi, props.invoice?.tier)
    writeCsv(localFileName, csvData)
  }

  const [exportMenuAnchorElem, setExportMenuAnchorElem] =
    React.useState<null | HTMLElement>(null)
  const isExportMenuOpened = Boolean(exportMenuAnchorElem)
  const handleExportButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchorElem(event.currentTarget)
  }
  const handleExportMenuClose = () => {
    setExportMenuAnchorElem(null)
  }

  return (
    <div className={fullClassName}>
      <div className="usage-table-header">
        <div className="title-description">
          {props.title && (
            <div className="billing-component-title">{props.title}</div>
          )}
          <Description />
        </div>

        <div className="export-button-container">
          <Button
            label="Export"
            className={'cursor-pointer'}
            dark
            onClick={handleExportButtonClick}
          />
          <Menu
            id="export-menu"
            anchorEl={exportMenuAnchorElem}
            open={isExportMenuOpened}
            onClose={handleExportMenuClose}
            // Avoid shift of body to the left when opening the drop-down menu
            // due to the add of right-padding
            // to compensate scroll bar appearance
            disableScrollLock={true}
            slotProps={{
              list: {
                'aria-labelledby': 'export-menu-button',
              },
            }}
          >
            <MenuItem
              onClick={() => {
                handleExportMenuClose()
                exportKpiToCsv(selectedKpi)
              }}
              sx={{ fontSize: EXPORT_MENU_FONT_SIZE }}
            >
              Export selected month
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleExportMenuClose()
                exportKpiToCsv()
              }}
              sx={{ fontSize: EXPORT_MENU_FONT_SIZE }}
            >
              Export selected year
            </MenuItem>
          </Menu>
        </div>
      </div>

      <div className="total-date-picker-container">
        <UsageChart
          className={'total-usage-chart'}
          kpiList={props.kpiList}
          selectedDate={selectedKpi?.periodStart}
          onBarClick={(year: number, monthIndex: number) => {
            if (!props.invoice) return

            const clickDate = new Date(year, monthIndex)
            const [index] = props.invoice.findTaskDetailByDate(
              clickDate,
              selectedKpi ? selectedKpi.periodStart : new Date(),
              false
            )
            if (index !== null) {
              handleKpiSelectionIndexChange(index)
            }
          }}
        />

        <div className="total-container">
          <ValueComp
            title="Selected period"
            valueComponent={
              <UsagePeriodSelect
                invoice={props.invoice}
                selectedIndex={selectedKpiIndex}
                onChange={handleKpiSelectionChange}
                disabled={!isKpiHistoryAvailable}
              />
            }
          />

          {/*
             Display the estimation of the total for accounts types
             other than Top-Up or Enterprise
             */}
          {TierType.TopUpCreditAimd != props.invoice?.tier &&
            !AccountUtil.isTierEnterprise(props.invoice?.tier) && (
              <ValueComp
                title="Estimated total"
                formattedValue={TextUtil.formatPrice(
                  props.invoice?.totalCost ?? 0,
                  props.invoice?.currency ?? '€',
                  true
                )}
              />
            )}
          <ValueComp
            title={
              <>
                Number of
                <br />
                processed tracks
              </>
            }
            formattedValue={`${selectedKpi?.allTrackProcessedCount ?? 0}`}
          />
        </div>
      </div>

      <UsageTaskTable taskDetailList={selectedKpi?.taskDetailList ?? {}} />
    </div>
  )
}

export default UsageTab
